import { Box, Cpu, Database, Users } from "lucide-react";
import { Link } from "react-router";
import { OpenSourceBanner } from "../../components/marketing/OpenSourceBanner";
import { docsPath } from "./docsNav";

const CAPABILITIES = [
  {
    title: "Managed products",
    body: "Create a product, reserve Hz × CCU from your approved account's capacity budget (250 units by default for Lite), and follow its lifecycle to Running.",
    status: "Available",
    icon: Users,
  },
  {
    title: "Native Rust client",
    body: "Reveal a connection descriptor, then connect over native QUIC with verified TLS, Bearer authentication, and managed admission.",
    status: "Available",
    icon: Box,
  },
  {
    title: "TypeScript browser client",
    body: "Connect through the managed browser WebTransport endpoint with the TypeScript client, WVN1, verified transport metadata, and Bearer admission.",
    status: "Online",
    icon: Box,
  },
  {
    title: "Realtime events",
    body: "Publish Ephemeral events through the fixed logical space and channel returned for the managed session.",
    status: "Available",
    icon: Database,
  },
  {
    title: "Managed spatial routing",
    body: "Add up to 64 bounded 3D Spatial Subspaces per server and use their definitions from the connection descriptor.",
    status: "Online",
    icon: Cpu,
  },
] as const;

export function DocsIntroductionPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Getting started</p>
      <h1 id="what-is-woven-host" className="docs-page-title docs-page-title--hero">
        Realtime sessions, <br />
        managed with WOVEN Host.
      </h1>
      <p className="docs-lede">
        WOVEN Host provides the active managed path for creating a product, bringing its realtime
        runtime to Running, and connecting native Rust or TypeScript browser clients. Browser
        WebTransport and managed 3D spatial routing are Online.
      </p>

      <h2 id="available-now" className="docs-heading">
        What is available now
      </h2>
      <div className="docs-feature-grid">
        {CAPABILITIES.map((capability) => (
          <div className="panel docs-feature-card" key={capability.title}>
            <div className="docs-topic-card-head">
              <capability.icon
                className="docs-feature-icon"
                size={22}
                strokeWidth={1.5}
                absoluteStrokeWidth
                aria-hidden="true"
              />
              <span className="badge badge--mock">{capability.status}</span>
            </div>
            <h3 className="docs-feature-title">{capability.title}</h3>
            <p className="docs-feature-body">{capability.body}</p>
          </div>
        ))}
      </div>

      <OpenSourceBanner />

      <h2 id="get-started" className="docs-heading">
        Get started
      </h2>
      <p className="docs-body">
        New sign-ins remain guest with zero capacity until approved. The approved Lite default is
        250 capacity units: for example, 25 concurrent users at 10 Hz. Pro defaults to 2,500 units;
        custom approved budgets are preserved. Create a product in the console, wait until it is
        Running, and explicitly reveal its connection descriptor. The descriptor is the source of
        truth for client endpoints, credentials, trust information, and managed session identifiers.
      </p>
      <Link to={docsPath("quickstart")} className="btn btn--ghost docs-continue-btn">
        Continue to quickstart &rarr;
      </Link>

      <h2 id="client-support" className="docs-heading">
        Client support
      </h2>
      <p className="docs-body">
        Rust clients use native QUIC. The Online TypeScript client carries WVN1 over browser
        WebTransport and HTTP/3/QUIC to the same managed runtime through the descriptor&apos;s
        explicit browser endpoint. Follow the{" "}
        <Link to={docsPath("sdks/rust")} className="docs-inline-link">
          Rust client guide
        </Link>{" "}
        or the{" "}
        <Link to={docsPath("sdks/javascript")} className="docs-inline-link">
          TypeScript client guide
        </Link>{" "}
        for the applicable managed flow and availability boundary.
      </p>

      <h2 id="preview" className="docs-heading">
        Preview scope
      </h2>
      <p className="docs-body">
        Pro and Dedicated are Preview tiers offered through private founder access first.
        Persistence beyond Ephemeral and Inference remain Preview capabilities. Spatial Subspaces,
        managed spatial routing, browser WebTransport, and the TypeScript browser client are Online.
      </p>
      <Link to={docsPath("core-concepts")} className="docs-inline-link">
        Review the core concepts &rarr;
      </Link>
    </div>
  );
}
